import { useLocation } from 'react-router-dom';

const WIKI = {
  '/dashboard': {
    titulo: 'Dashboard',
    secoes: [
      {
        titulo: 'O que é',
        texto: 'Painel central com o estado atual da sua frota de instrumentos. Atualiza automaticamente a cada acesso.',
      },
      {
        titulo: 'Indicadores (KPIs)',
        itens: [
          { label: 'Calibrado', desc: 'Instrumentos com calibração válida e dentro do prazo.' },
          { label: 'A vencer', desc: 'Calibração expira dentro do período de alerta configurado (ex: 45 dias). Ação recomendada.' },
          { label: 'Vencido', desc: 'Prazo de calibração ultrapassado. O instrumento pode ser bloqueado automaticamente.' },
          { label: 'Bloqueado', desc: 'Instrumento impedido de uso até nova calibração aprovada.' },
        ],
      },
      {
        titulo: 'Alertas',
        texto: 'O período de antecedência dos alertas é configurável em Configurações → Alertas. Instrumentos a vencer aparecem em amarelo; vencidos em vermelho.',
      },
    ],
  },

  '/instruments': {
    titulo: 'Instrumentos',
    secoes: [
      {
        titulo: 'TAG',
        texto: 'Código único do instrumento na planta. Formato recomendado: AAA-0000 (ex: TI-2301 para transmissor de temperatura). Deve ser único no sistema.',
      },
      {
        titulo: 'Status de calibração',
        itens: [
          { label: 'Calibrado', desc: 'Calibração válida, dentro do prazo.' },
          { label: 'A vencer', desc: 'Dentro do período de alerta configurado.' },
          { label: 'Vencido', desc: 'Prazo expirado — requer nova calibração.' },
          { label: 'Aguardando laudo', desc: 'Calibração registrada mas laudo ainda não assinado.' },
          { label: 'Em calibração', desc: 'Instrumento enviado ao laboratório.' },
          { label: 'Não calibrado', desc: 'Nunca calibrado ou sem registro.' },
          { label: 'Bloqueado', desc: 'Uso suspenso administrativamente.' },
        ],
      },
      {
        titulo: 'Criticalidade',
        texto: 'Classifica o impacto da falha do instrumento: Alta (segurança/qualidade crítica), Média (impacto moderado), Baixa (informativo).',
      },
      {
        titulo: 'Faixas de medição',
        texto: 'Faixa de medição: capacidade total do instrumento. Faixa de uso: intervalo utilizado no processo. Faixa nominal: referência do fabricante.',
      },
    ],
  },

  '/calibrations': {
    titulo: 'Calibrações',
    secoes: [
      {
        titulo: 'Fluxo do laudo',
        itens: [
          { label: 'Rascunho', desc: 'Em edição — pode ser alterado livremente.' },
          { label: 'Em revisão', desc: 'Enviado para o revisor assinar.' },
          { label: 'Concluído', desc: 'Assinado e imutável. Conforme ISO 10012 §7.2.' },
          { label: 'Rejeitado', desc: 'Revisor devolveu com motivo. Volta para rascunho.' },
        ],
      },
      {
        titulo: 'Grupos de medição',
        texto: 'Cada grandeza medida (temperatura, pressão, etc.) forma um grupo com seus pontos de calibração. O EMP define o critério de aprovação de cada ponto.',
      },
      {
        titulo: 'Incerteza expandida',
        texto: 'Intervalo que contém o valor verdadeiro com probabilidade declarada. U = k × uc, onde k é o fator de abrangência (normalmente k=2 para 95%). Obrigatória em laudos modo "Completo".',
      },
      {
        titulo: 'EMP — Erro Máximo Permissível',
        texto: 'Limite de erro aceitável definido pelo fabricante ou pela empresa. Um ponto é aprovado quando |erro| ≤ EMP. Herdado automaticamente do cadastro do instrumento.',
      },
      {
        titulo: 'Importação por IA',
        texto: 'Envie uma foto ou PDF do certificado de calibração. A IA extrai automaticamente TAG, pontos (aplicado/lido/erro/incerteza) e dados do padrão.',
      },
    ],
  },

  '/padroes-referencia': {
    titulo: 'Padrões de Referência',
    secoes: [
      {
        titulo: 'O que são',
        texto: 'Instrumentos com certificado de calibração rastreável usados para calibrar outros instrumentos. Ex: termômetro padrão RBC, manômetro padrão.',
      },
      {
        titulo: 'Tipos (hierarquia metrológica)',
        itens: [
          { label: 'Primário', desc: 'Máxima exatidão, rastreável diretamente ao SI (INMETRO/BIPM). Raramente na indústria.' },
          { label: 'Secundário', desc: 'Calibrado contra um primário. Típico de laboratórios RBC.' },
          { label: 'Trabalho', desc: 'O mais comum. Usado no dia a dia para calibrar instrumentos do processo.' },
          { label: 'Transferência', desc: 'Intermediário entre laboratórios quando a comparação direta não é possível.' },
        ],
      },
      {
        titulo: 'Rastreabilidade',
        texto: 'Cadeia ininterrupta de comparações de um instrumento até um padrão nacional ou internacional. Exigida pela ISO 10012 §7.5. O sistema alerta automaticamente quando o certificado do padrão vence.',
      },
      {
        titulo: 'Incerteza do padrão (u_B)',
        texto: 'u_B = U_padrão / k, onde U é a incerteza expandida do certificado e k é o fator de abrangência. Usada no cálculo GUM da calibração.',
      },
    ],
  },

  '/reports': {
    titulo: 'Relatórios',
    secoes: [
      {
        titulo: 'Tipos disponíveis',
        itens: [
          { label: 'Por instrumento', desc: 'Histórico completo de calibrações de um instrumento específico.' },
          { label: 'Por período', desc: 'Todos os laudos emitidos em um intervalo de datas.' },
          { label: 'Vencimentos', desc: 'Instrumentos que vencerão em X dias — útil para planejamento.' },
          { label: 'Rastreabilidade', desc: 'Cadeia de rastreabilidade de um instrumento até o padrão nacional.' },
        ],
      },
      {
        titulo: 'Exportação',
        texto: 'Cada relatório pode ser exportado em PDF (para arquivamento) ou Excel (para análise). O PDF inclui o cabeçalho e rodapé configurados em Configurações → Identidade.',
      },
    ],
  },

  '/loops': {
    titulo: 'Malhas de Medição',
    secoes: [
      {
        titulo: 'O que são',
        texto: 'Grupos de instrumentos que formam um loop de controle ou medição no processo. Ex: malha de temperatura do reator R-101 composta por sensor (TE), transmissor (TT) e indicador (TI).',
      },
      {
        titulo: 'Por que cadastrar',
        texto: 'Permite rastrear todos os instrumentos de uma malha de uma vez e identificar quais malhas estão com instrumentos vencidos ou bloqueados.',
      },
    ],
  },

  '/classes': {
    titulo: 'Classes de Instrumento',
    secoes: [
      {
        titulo: 'O que são',
        texto: 'Modelos de configuração compartilhados entre instrumentos similares. Definem frequência de calibração e critério de aceitação padrão (EMP).',
      },
      {
        titulo: 'Como funciona',
        texto: 'Ao vincular um instrumento a uma classe, ele herda automaticamente a frequência de calibração e o EMP. Alterar a classe atualiza todos os instrumentos vinculados.',
      },
    ],
  },

  '/calendar': {
    titulo: 'Calendário',
    secoes: [
      {
        titulo: 'O que mostra',
        texto: 'Visualização mensal dos vencimentos de calibração. Cada dia com instrumentos a vencer é marcado — clique para ver quais são.',
      },
      {
        titulo: 'Planejamento',
        texto: 'Use para programar calibrações antes do vencimento. Instrumentos com alerta configurado aparecem X dias antes da data real.',
      },
    ],
  },

  '/settings': {
    titulo: 'Configurações',
    secoes: [
      {
        titulo: 'Seções disponíveis',
        itens: [
          { label: 'Permissões', desc: 'Define o que cada perfil pode ver, editar ou assinar.' },
          { label: 'Identidade', desc: 'Nome da empresa e logo que aparecem nos laudos PDF.' },
          { label: 'Datas', desc: 'Formato de exibição e critério de vencimento.' },
          { label: 'Alertas', desc: 'Com quantos dias de antecedência o sistema avisa sobre vencimentos.' },
          { label: 'Calibrações', desc: 'Modo de aprovação (GUM completo ou simplificado) e tipos permitidos.' },
          { label: 'Escalas', desc: 'Grandezas e unidades disponíveis para seleção nos instrumentos.' },
          { label: 'Laboratórios', desc: 'Fornecedores externos de calibração.' },
          { label: 'POPs', desc: 'Procedimentos operacionais padrão referenciados nos laudos.' },
        ],
      },
    ],
  },

  '/usuarios': {
    titulo: 'Usuários',
    secoes: [
      {
        titulo: 'Perfis de acesso',
        itens: [
          { label: 'Supervisor', desc: 'Acesso total — gerencia usuários, configurações e todos os dados.' },
          { label: 'Operador', desc: 'Cria e edita instrumentos e calibrações.' },
          { label: 'Revisor', desc: 'Assina laudos de calibração (ação irreversível).' },
          { label: 'Consulta', desc: 'Somente leitura — nenhuma ação de edição.' },
        ],
      },
      {
        titulo: 'Boas práticas',
        texto: 'Nunca compartilhe credenciais entre pessoas. Desative contas ao invés de excluí-las para manter o histórico de auditoria.',
      },
    ],
  },

  '/audit': {
    titulo: 'Audit Trail',
    secoes: [
      {
        titulo: 'O que é',
        texto: 'Registro imutável de todas as ações realizadas no sistema: criações, edições, exclusões e assinaturas. Exigido pela ISO 10012 para rastreabilidade de alterações.',
      },
      {
        titulo: 'Filtros',
        texto: 'Filtre por entidade (instrumento, calibração...), operação (criar, editar, assinar) ou período. Útil para auditorias e investigação de problemas.',
      },
    ],
  },
};

function getWiki(pathname) {
  if (pathname.startsWith('/instruments')) return WIKI['/instruments'];
  if (pathname.startsWith('/calibrations')) return WIKI['/calibrations'];
  return WIKI[pathname] || null;
}

export default function HelpPanel({ open, onClose }) {
  const { pathname } = useLocation();
  const wiki = getWiki(pathname);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      <div className="fixed top-0 right-0 h-full w-[360px] z-50 flex flex-col bg-[var(--surface-2)] border-l border-[var(--surface-4)] shadow-2xl">

        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--surface-4)] flex-shrink-0">
          <span className="text-base">📖</span>
          <span className="text-sm font-semibold text-slate-100 flex-1">
            {wiki ? `Ajuda — ${wiki.titulo}` : 'Ajuda'}
          </span>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors text-lg leading-none">✕</button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {!wiki ? (
            <p className="text-xs text-slate-500 text-center pt-8">
              Nenhum conteúdo de ajuda disponível para esta página ainda.
            </p>
          ) : (
            wiki.secoes.map((s, i) => (
              <div key={i}>
                <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">{s.titulo}</h3>
                {s.texto && (
                  <p className="text-xs text-slate-400 leading-relaxed">{s.texto}</p>
                )}
                {s.itens && (
                  <div className="space-y-2">
                    {s.itens.map((item, j) => (
                      <div key={j} className="flex gap-2">
                        <span className="text-xs font-semibold text-slate-200 whitespace-nowrap">{item.label}:</span>
                        <span className="text-xs text-slate-400 leading-relaxed">{item.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
